import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail, Search, Filter, RefreshCw, Plus, Sparkles, X, Check, ArrowRight,
  User, Phone, MapPin,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { useAuth } from "../../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function formatDateShort(dt) {
  if (!dt) return "";
  try {
    const d = new Date(dt);
    const isToday = new Date().toDateString() === d.toDateString();
    if (isToday) return d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch { return dt; }
}

const AUTOSUFFICIENZA_OPT = ["Autosufficiente", "Parzialmente autosufficiente", "Non autosufficiente"];
const URGENZA_OPT = ["Urgente (entro 1 settimana)", "Entro 1 mese", "Entro 3 mesi", "Senza urgenza"];
const TIPO_POSTO_OPT = ["Singola", "Doppia", "RSA", "Nucleo Alzheimer", "Lungodegenza", "Centro diurno"];
const RELAZIONE_OPT = ["Figlio/a", "Coniuge", "Nipote", "Fratello/Sorella", "Altro familiare", "Tutore legale", "Medico curante", "Assistente sociale"];

const STATI_EMAIL = ["Non importata", "Importata"];

export default function ImportaEmailDialog({ open, onOpenChange, onImportato }) {
  const { token } = useAuth();
  const authHeader = { Authorization: `Bearer ${token}` };

  const [emails, setEmails] = useState([]);
  const [loadingEmails, setLoadingEmails] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [filtroStato, setFiltroStato] = useState("Non importata");
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [fields, setFields] = useState({});
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [manualText, setManualText] = useState("");
  const [manualMode, setManualMode] = useState(false);

  const fetchEmails = useCallback(async () => {
    setLoadingEmails(true);
    try {
      const res = await axios.get(`${API_URL}/api/ospiti-rete/email/inbox`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmails(res.data.emails || []);
    } catch {
      // Se nessuna email nella inbox, mostra lista vuota
      setEmails([]);
    } finally {
      setLoadingEmails(false);
    }
  }, [token]);

  useEffect(() => {
    if (open) fetchEmails();
  }, [open, fetchEmails]);

  const handleSelectEmail = async (email) => {
    setSelectedEmail(email);
    setFields({});
    if (email) {
      await parseEmail(email.testo || "", email.oggetto, email.da);
    }
  };

  const parseEmail = async (testo, oggetto = "", mittente = "") => {
    if (!testo.trim()) return;
    setParsing(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ospiti-rete/email/parse`,
        { testo, oggetto, mittente },
        { headers: authHeader }
      );
      setFields(res.data.fields || {});
    } catch {
      toast.error("Errore nell'estrazione AI");
    } finally {
      setParsing(false);
    }
  };

  const parseManual = () => parseEmail(manualText);

  const setField = (key, value) => setFields((prev) => ({ ...prev, [key]: value }));

  const handleImporta = async () => {
    if (!fields.nome_referente || !fields.cognome_referente || !fields.telefono) {
      toast.error("Compila almeno nome, cognome e telefono del referente");
      return;
    }
    setImporting(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/ospiti-rete/email/import`,
        { fields, email_id: selectedEmail?.id },
        { headers: authHeader }
      );
      toast.success("Richiesta creata dall'email");
      onImportato?.(res.data.richiesta);
      // Refresh inbox
      fetchEmails();
      setSelectedEmail(null);
      setFields({});
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore nell'importazione");
    } finally {
      setImporting(false);
    }
  };

  const filteredEmails = emails.filter((e) => {
    const importata = e.importata ? "Importata" : "Non importata";
    if (filtroStato !== "Tutti" && importata !== filtroStato) return false;
    if (searchQ) {
      const q = searchQ.toLowerCase();
      return (
        (e.da || "").toLowerCase().includes(q) ||
        (e.oggetto || "").toLowerCase().includes(q) ||
        (e.anteprima || "").toLowerCase().includes(q)
      );
    }
    return true;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden p-0">
        <DialogHeader className="px-5 py-4 border-b border-slate-200">
          <DialogTitle className="text-xl font-bold flex items-center gap-2 text-slate-900">
            <Mail className="w-5 h-5 text-blue-600" />
            Importa da email
          </DialogTitle>
          <p className="text-sm text-slate-500">
            Seleziona le email contenenti richieste di posto letto e crea nuove richieste automaticamente.
          </p>
        </DialogHeader>

        <div className="flex h-[75vh]">
          {/* Colonna 1: Lista email + account */}
          <div className="w-72 border-r border-slate-200 flex flex-col bg-slate-50">
            {/* Account */}
            <div className="p-3 border-b border-slate-200 bg-white">
              <Label className="text-xs text-slate-500">1. Account email</Label>
              <Select defaultValue="inbound">
                <SelectTrigger className="mt-1 h-8 text-xs">
                  <Mail className="w-3.5 h-3.5 mr-1.5 text-slate-500" />
                  <SelectValue placeholder="Seleziona account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="inbound" className="text-xs">Casella Affidya (inbound)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Cerca + Filtro */}
            <div className="p-3 border-b border-slate-200 space-y-2 bg-white">
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  className="pl-8 h-7 text-xs"
                  placeholder="Cerca email..."
                  value={searchQ}
                  onChange={(e) => setSearchQ(e.target.value)}
                />
              </div>
              <div className="flex gap-1">
                {["Non importata", "Importata", "Tutti"].map((s) => (
                  <button
                    key={s}
                    onClick={() => setFiltroStato(s)}
                    className={`px-2 py-0.5 rounded text-xs transition-colors ${
                      filtroStato === s ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>

            {/* Opzione modalità manuale */}
            <button
              onClick={() => { setManualMode(!manualMode); setSelectedEmail(null); setFields({}); }}
              className={`flex items-center gap-2 px-3 py-2.5 text-xs border-b border-slate-200 transition-colors ${
                manualMode ? "bg-blue-50 text-blue-700" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              <Sparkles className="w-3.5 h-3.5" />
              Incolla testo email (manuale)
            </button>

            {/* Lista email */}
            <div className="flex-1 overflow-y-auto">
              {loadingEmails ? (
                <div className="flex justify-center py-6">
                  <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" />
                </div>
              ) : filteredEmails.length === 0 ? (
                <div className="p-4 text-center">
                  <Mail className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">
                    {emails.length === 0
                      ? "Nessuna email inbound ricevuta.\nUsa la modalità manuale."
                      : "Nessuna email corrisponde ai filtri."}
                  </p>
                </div>
              ) : (
                filteredEmails.map((email) => (
                  <button
                    key={email.id}
                    onClick={() => { setManualMode(false); handleSelectEmail(email); }}
                    className={`w-full text-left p-3 border-b border-slate-100 transition-colors ${
                      selectedEmail?.id === email.id
                        ? "bg-blue-50 border-l-2 border-l-blue-500"
                        : "hover:bg-white"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <p className="font-medium text-xs text-slate-800 truncate flex-1">{email.da || "Mittente sconosciuto"}</p>
                      <span className="text-xs text-slate-400 shrink-0">{formatDateShort(email.received_at)}</span>
                    </div>
                    <p className="text-xs text-slate-600 truncate mt-0.5">{email.oggetto || "(senza oggetto)"}</p>
                    <p className="text-xs text-slate-400 truncate mt-0.5">{email.anteprima || ""}</p>
                    {email.importata && (
                      <Badge className="mt-1 bg-emerald-100 text-emerald-700 text-xs h-4 px-1">Importata</Badge>
                    )}
                  </button>
                ))
              )}
            </div>

            {/* Selezione multipla */}
            {selectedIds.length > 0 && (
              <div className="p-2 bg-blue-600 text-white text-xs flex items-center justify-between">
                <span>Selezionate: {selectedIds.length}</span>
                <Button size="sm" variant="ghost" className="h-6 text-white hover:text-white hover:bg-blue-700 text-xs">
                  Importa selezionate
                </Button>
              </div>
            )}
          </div>

          {/* Colonna 2: Dati estratti modificabili */}
          <div className="flex-1 flex flex-col border-r border-slate-200 overflow-y-auto">
            {!selectedEmail && !manualMode ? (
              <div className="flex-1 flex items-center justify-center text-center p-8">
                <div>
                  <Mail className="w-12 h-12 text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-400 text-sm">Seleziona un'email a sinistra o usa la modalità manuale per estrarre i dati automaticamente</p>
                </div>
              </div>
            ) : (
              <div className="p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-700">2. Dati estratti automaticamente</h3>
                  {parsing && (
                    <div className="flex items-center gap-1.5 text-xs text-blue-600">
                      <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                      Estrazione AI in corso...
                    </div>
                  )}
                  {!parsing && (selectedEmail || manualMode) && (
                    <div className="flex items-center gap-1 text-xs text-slate-500">
                      <span>Fonte rilevata:</span>
                      <Badge className="bg-slate-100 text-slate-600 text-xs">{selectedEmail ? "Email" : "Testo manuale"}</Badge>
                      {selectedEmail && (
                        <button className="text-blue-600 hover:underline ml-1" onClick={() => handleSelectEmail(selectedEmail)}>
                          Rianalizza
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {manualMode && (
                  <div className="space-y-2">
                    <Label className="text-xs">Incolla il testo dell'email</Label>
                    <Textarea
                      rows={4}
                      placeholder="Incolla qui il corpo dell'email con la richiesta di posto letto..."
                      className="text-xs"
                      value={manualText}
                      onChange={(e) => setManualText(e.target.value)}
                    />
                    <Button
                      size="sm"
                      className="h-7 text-xs bg-blue-600 hover:bg-blue-700"
                      onClick={parseManual}
                      disabled={parsing || !manualText.trim()}
                    >
                      {parsing ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                      Estrai dati con AI
                    </Button>
                  </div>
                )}

                {/* Form dati estratti */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Nome referente <span className="text-red-500">*</span></Label>
                    <Input className="mt-1 h-8 text-xs" value={fields.nome_referente || ""} onChange={(e) => setField("nome_referente", e.target.value)} placeholder="Nome" />
                  </div>
                  <div>
                    <Label className="text-xs">Cognome referente <span className="text-red-500">*</span></Label>
                    <Input className="mt-1 h-8 text-xs" value={fields.cognome_referente || ""} onChange={(e) => setField("cognome_referente", e.target.value)} placeholder="Cognome" />
                  </div>
                  <div>
                    <Label className="text-xs">Telefono <span className="text-red-500">*</span></Label>
                    <Input className="mt-1 h-8 text-xs" value={fields.telefono || ""} onChange={(e) => setField("telefono", e.target.value)} placeholder="Telefono" />
                  </div>
                  <div>
                    <Label className="text-xs">Email</Label>
                    <Input className="mt-1 h-8 text-xs" value={fields.email_referente || ""} onChange={(e) => setField("email_referente", e.target.value)} placeholder="email@esempio.it" />
                  </div>
                  <div>
                    <Label className="text-xs">Relazione con l'ospite</Label>
                    <Select value={fields.relazione_ospite || ""} onValueChange={(v) => setField("relazione_ospite", v)}>
                      <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>{RELAZIONE_OPT.map((v) => <SelectItem key={v} value={v} className="text-xs">{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Nome ospite</Label>
                    <Input className="mt-1 h-8 text-xs" value={fields.nome_ospite || ""} onChange={(e) => setField("nome_ospite", e.target.value)} placeholder="Nome e cognome" />
                  </div>
                  <div>
                    <Label className="text-xs">Età</Label>
                    <Input className="mt-1 h-8 text-xs" value={fields.eta_ospite || ""} onChange={(e) => setField("eta_ospite", e.target.value)} placeholder="Es. 82" />
                  </div>
                  <div>
                    <Label className="text-xs">Autosufficienza</Label>
                    <Select value={fields.autosufficienza || ""} onValueChange={(v) => setField("autosufficienza", v)}>
                      <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>{AUTOSUFFICIENZA_OPT.map((v) => <SelectItem key={v} value={v} className="text-xs">{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Patologie principali</Label>
                    <Input className="mt-1 h-8 text-xs" value={fields.patologie || ""} onChange={(e) => setField("patologie", e.target.value)} placeholder="Es. Ipertensione, Diabete" />
                  </div>
                  <div>
                    <Label className="text-xs">Urgenza inserimento</Label>
                    <Select value={fields.urgenza_inserimento || ""} onValueChange={(v) => setField("urgenza_inserimento", v)}>
                      <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>{URGENZA_OPT.map((v) => <SelectItem key={v} value={v} className="text-xs">{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Tipo posto richiesto</Label>
                    <Select value={fields.tipo_posto || ""} onValueChange={(v) => setField("tipo_posto", v)}>
                      <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                      <SelectContent>{TIPO_POSTO_OPT.map((v) => <SelectItem key={v} value={v} className="text-xs">{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Zona richiesta</Label>
                    <Input className="mt-1 h-8 text-xs" value={fields.zona || ""} onChange={(e) => setField("zona", e.target.value)} placeholder="Es. Bari e provincia" />
                  </div>
                  <div>
                    <Label className="text-xs">Budget indicativo</Label>
                    <Input className="mt-1 h-8 text-xs" value={fields.budget || ""} onChange={(e) => setField("budget", e.target.value)} placeholder="Es. 2.000 €" />
                  </div>
                  <div>
                    <Label className="text-xs">Fonte richiesta</Label>
                    <Input className="mt-1 h-8 text-xs" value={fields.fonte || "Email"} onChange={(e) => setField("fonte", e.target.value)} />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Stato</Label>
                    <Select value={fields.stato || "Nuova"} onValueChange={(v) => setField("stato", v)}>
                      <SelectTrigger className="mt-1 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["Nuova", "Da seguire"].map((v) => <SelectItem key={v} value={v} className="text-xs">{v}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs">Note interne</Label>
                    <Textarea className="mt-1 text-xs" rows={2} value={fields.note || ""} onChange={(e) => setField("note", e.target.value)} placeholder="Note estratte o aggiunte manualmente..." />
                  </div>
                </div>

                <div className="pt-2">
                  <Button
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white gap-1.5"
                    onClick={handleImporta}
                    disabled={importing || !fields.nome_referente || !fields.cognome_referente || !fields.telefono}
                  >
                    {importing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                    Crea richiesta
                  </Button>
                </div>
              </div>
            )}
          </div>

          {/* Colonna 3: Anteprima email */}
          <div className="w-72 flex flex-col bg-white">
            <div className="p-3 border-b border-slate-200">
              <h3 className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Anteprima email</h3>
            </div>
            {selectedEmail ? (
              <div className="flex-1 overflow-y-auto p-3 space-y-2">
                <div className="space-y-1 text-xs pb-2 border-b border-slate-100">
                  <div className="flex gap-1">
                    <span className="text-slate-400 w-8 shrink-0">Da</span>
                    <span className="text-slate-700 break-all">{selectedEmail.da}</span>
                  </div>
                  <div className="flex gap-1">
                    <span className="text-slate-400 w-8 shrink-0">A</span>
                    <span className="text-slate-500">{selectedEmail.a}</span>
                  </div>
                  <div className="flex gap-1">
                    <span className="text-slate-400 w-8 shrink-0">Ogg.</span>
                    <span className="text-slate-700 font-medium">{selectedEmail.oggetto}</span>
                  </div>
                  {selectedEmail.received_at && (
                    <div className="flex gap-1">
                      <span className="text-slate-400 w-8 shrink-0">Data</span>
                      <span className="text-slate-500">{new Date(selectedEmail.received_at).toLocaleString("it-IT")}</span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {selectedEmail.testo || selectedEmail.anteprima || "(nessun testo)"}
                </p>
              </div>
            ) : manualMode ? (
              <div className="flex-1 flex items-center justify-center text-center p-6">
                <p className="text-xs text-slate-400">Incolla il testo nella colonna centrale e clicca "Estrai dati con AI"</p>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-center p-6">
                <p className="text-xs text-slate-400">Seleziona un'email dalla lista per vedere il contenuto</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
